import React, { useCallback, useMemo, useState } from 'react';

import { Box, Divider, Drawer, IconButton, ListItemButton, Typography } from '@mui/material';
import { CancelIcon, PlayIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { resolveColor } from 'constants/colors';
import { IconSize } from 'constants/coreDesignSystem';
import { getIconComponentByValue } from 'constants/iconOptions';

import { hasExecutionPrompt } from '../../LoanInfoHeader/LoanActionsControls';

import ConfirmationView from './ConfirmationView';
import {
  actionItemSx,
  actionNameSx,
  closeButtonSx,
  defaultIconWrapperSx,
  disabledActionItemSx,
  drawerContentSx,
  drawerPaperProps,
  groupDividerSx,
  handleBarSx,
  headerSx,
  iconWrapperSx,
  rowDividerSx,
  sectionHeaderSx,
  titleSx,
} from './MobileLoanActionsDrawer.styles';
import { MobileLoanActionsDrawerTestIds } from './MobileLoanActionsDrawer.testIds';
import { getAccentBorderSx, getIconBackground } from './mobileLoanActionsDrawer.utils';

const ActionItem = ({ actionData, isDisabled, onSelectAction }) => {
  const IconComponent = useMemo(() => getIconComponentByValue(actionData.icon), [actionData.icon]);

  const resolvedColor = useMemo(() => resolveColor(actionData.color), [actionData.color]);

  const iconBg = useMemo(
    () => getIconBackground({ resolvedColor, variant: actionData.variant }),
    [resolvedColor, actionData.variant],
  );

  const hasCustomColor = Object.keys(iconBg).length > 0;

  const mergedIconSx = useMemo(() => {
    if (hasCustomColor) {
      return { ...iconWrapperSx, ...iconBg };
    }

    return defaultIconWrapperSx;
  }, [iconBg, hasCustomColor]);

  const accentBorder = useMemo(() => getAccentBorderSx(resolvedColor), [resolvedColor]);

  const itemSx = useMemo(() => {
    const base = isDisabled ? disabledActionItemSx : actionItemSx;

    return { ...base, ...accentBorder };
  }, [isDisabled, accentBorder]);

  const handleClick = useCallback(() => {
    onSelectAction(actionData);
  }, [actionData, onSelectAction]);

  return (
    <ListItemButton disabled={isDisabled} sx={itemSx} onClick={handleClick}>
      <Box sx={mergedIconSx}>{IconComponent ? <IconComponent /> : <PlayIcon />}</Box>
      <Typography sx={actionNameSx} variant="regular14">
        {actionData.name}
      </Typography>
    </ListItemButton>
  );
};

ActionItem.propTypes = {
  actionData: PropTypes.object.isRequired,
  onSelectAction: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

const MobileLoanActionsDrawer = ({
  open,
  onClose,
  groupedActions,
  executingActionId = null,
  disabledLoanActionsSet,
  onExecuteAction,
}) => {
  const [pendingAction, setPendingAction] = useState(null);

  const handleSelectAction = useCallback(
    actionData => {
      if (hasExecutionPrompt(actionData)) {
        onExecuteAction(actionData.id);
        onClose();

        return;
      }

      setPendingAction(actionData);
    },
    [onExecuteAction, onClose],
  );

  const handleCancelConfirmation = useCallback(() => {
    setPendingAction(null);
  }, []);

  const handleProceed = useCallback(
    actionId => {
      onExecuteAction(actionId);
      setPendingAction(null);
      onClose();
    },
    [onExecuteAction, onClose],
  );

  const handleDrawerClose = useCallback(() => {
    setPendingAction(null);
    onClose();
  }, [onClose]);

  const hasNamedGroups = groupedActions.namedGroups.length > 0;

  return (
    <Drawer
      PaperProps={drawerPaperProps}
      anchor="bottom"
      data-testid={MobileLoanActionsDrawerTestIds.DRAWER}
      open={open}
      onClose={handleDrawerClose}
    >
      <Box sx={handleBarSx} />
      <Box sx={headerSx}>
        <Typography sx={titleSx} variant="bold16">
          {pendingAction ? 'Confirm Action' : 'Loan Actions'}
        </Typography>
        <IconButton
          aria-label="close"
          data-testid={MobileLoanActionsDrawerTestIds.CLOSE_BUTTON}
          size="small"
          sx={closeButtonSx}
          onClick={handleDrawerClose}
        >
          <CancelIcon size={IconSize[20]} />
        </IconButton>
      </Box>
      {pendingAction ? (
        <ConfirmationView
          actionData={pendingAction}
          onCancel={handleCancelConfirmation}
          onProceed={handleProceed}
        />
      ) : (
        <Box sx={drawerContentSx}>
          {groupedActions.topLevel.map((actionData, index) => (
            <React.Fragment key={actionData.id}>
              {index > 0 && <Divider sx={rowDividerSx} />}
              <ActionItem
                actionData={actionData}
                isDisabled={
                  executingActionId === actionData.id || disabledLoanActionsSet.has(actionData.id)
                }
                onSelectAction={handleSelectAction}
              />
            </React.Fragment>
          ))}
          {hasNamedGroups && groupedActions.topLevel.length > 0 && <Divider sx={groupDividerSx} />}
          {groupedActions.namedGroups.map(([groupName, actions], groupIndex) => (
            <React.Fragment key={groupName}>
              {groupIndex > 0 && <Divider sx={groupDividerSx} />}
              <Typography sx={sectionHeaderSx} variant="bold12">
                {groupName}
              </Typography>
              {actions.map((actionData, index) => (
                <React.Fragment key={actionData.id}>
                  {index > 0 && <Divider sx={rowDividerSx} />}
                  <ActionItem
                    actionData={actionData}
                    isDisabled={
                      executingActionId === actionData.id ||
                      disabledLoanActionsSet.has(actionData.id)
                    }
                    onSelectAction={handleSelectAction}
                  />
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Drawer>
  );
};

MobileLoanActionsDrawer.propTypes = {
  disabledLoanActionsSet: PropTypes.instanceOf(Set).isRequired,
  groupedActions: PropTypes.shape({
    topLevel: PropTypes.array.isRequired,
    namedGroups: PropTypes.array.isRequired,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExecuteAction: PropTypes.func.isRequired,
  executingActionId: PropTypes.string,
};

export default MobileLoanActionsDrawer;
